/**
 * Builds share payloads (text + URL) for a public story across platforms.
 *
 * Two story shapes are supported:
 *   - sectioned: story.sections[] = [{key, label, content}, ...]
 *     story.story.turning_point holds section_3, the pivot in every template
 *     (Turning point / The insight / The teaching / The breakthrough).
 *   - raw: story.raw_text (free-flow narrative).
 *
 * Tag display labels (resolved from km_tags display_name by the caller) are
 * used to form a "From X to Y" framing line. Helpers fall back to the raw tag
 * IDs only when no resolver is provided.
 */

const EXCERPT_MAX = 320;
// Fallback pull-quote (derived from raw_text / sections) gets a length
// cap because the source is arbitrary-length prose. The author-curated
// `highlight` field already has a DB-level CHECK constraint of 280
// chars (see migration 010), so when we use it we show it in full.
const PULL_QUOTE_MAX = 160;
// Matches km_stories.highlight CHECK constraint. Surfaced here so the
// dialog's textarea + counter can pull from a single source.
export const HIGHLIGHT_MAX = 280;

const truncateAtBoundary = (text, max) => {
  if (!text) return '';
  const t = text.trim();
  if (t.length <= max) return t;
  const slice = t.slice(0, max);
  const lastSentence = Math.max(
    slice.lastIndexOf('. '),
    slice.lastIndexOf('! '),
    slice.lastIndexOf('? '),
    slice.lastIndexOf('。'),
  );
  if (lastSentence > max * 0.5) {
    return `${slice.slice(0, lastSentence + 1).trim()}…`;
  }
  const lastSpace = slice.lastIndexOf(' ');
  return `${(lastSpace > 0 ? slice.slice(0, lastSpace) : slice).trim()}…`;
};

const firstParagraph = (text) => {
  if (!text) return '';
  const parts = text.split(/\n{2,}/);
  return parts[0] || text;
};

const buildExcerpt = (story) => {
  // Author-curated highlight wins when present — that's the whole point of
  // the review screen. Fallback derives from story body for older rows or
  // when AI failed to produce one.
  const highlight = story.highlight?.trim();
  if (highlight) return truncateAtBoundary(highlight, EXCERPT_MAX);
  const sectioned = (story.sections?.length || 0) > 0;
  const source = sectioned
    ? story.story?.turning_point || story.sections[0]?.content
    : story.raw_text;
  return truncateAtBoundary(firstParagraph(source), EXCERPT_MAX);
};

// Cap visible labels per side so the from→to line stays readable.
// Three is fine — even with two-line wraps in the worst case
// ("A story of {A} & {B} & {C}"), the card still scans clean and the
// title above it already absorbs most of the visual weight. Anything
// beyond three starts to read as a comma-list and undercuts the
// "this is what shifted" framing.
const FROM_TO_MAX_PER_SIDE = 3;

const normalizeLabels = (labels) => {
  // Accept either the new shape ({ obstacles: [], qualities: [] }) or
  // the legacy singular shape ({ obstacle, quality }). Existing callers
  // that haven't migrated yet keep working.
  const rawObstacles = Array.isArray(labels?.obstacles)
    ? labels.obstacles
    : (labels?.obstacle ? [labels.obstacle] : []);
  const rawQualities = Array.isArray(labels?.qualities)
    ? labels.qualities
    : (labels?.quality ? [labels.quality] : []);
  const obstacles = rawObstacles.filter(Boolean).slice(0, FROM_TO_MAX_PER_SIDE);
  const qualities = rawQualities.filter(Boolean).slice(0, FROM_TO_MAX_PER_SIDE);
  return { obstacles, qualities };
};

const buildHashtags = (story, fromToLabels) => {
  const out = ['KathaMithra'];
  if (story.tradition) out.push(story.tradition.replace(/\s+/g, ''));
  const { qualities } = normalizeLabels(fromToLabels);
  for (const q of qualities) {
    out.push(q.replace(/[^\w]/g, ''));
  }
  return out.filter(Boolean).map((t) => `#${t}`).join(' ');
};

const authorSuffix = (story) => {
  const name = story.author_name;
  if (!name || name === 'Anonymous') return '';
  return ` — ${name}`;
};

/**
 * Build the "From obstacle → quality" display line from already-resolved
 * display labels. Returns '' when nothing useful is available.
 *
 * Accepts arrays of display names (cap of 2 per side). Single-label
 * legacy shape ({ obstacle, quality }) still supported via normalize.
 *
 * The `&` joiner reads as "and with equal weight" — two qualities that
 * emerged together, not a list of unrelated tags.
 *
 * Framing rules (the line should always read like a sentence):
 *   • obstacle(s) + quality(ies)  → `{obstacles}  →  {qualities}`
 *     The arrow IS the framing; no preamble needed.
 *   • quality(ies) only           → `A story of {qualities}`
 *   • obstacle(s) only            → `On {obstacles}`
 * Articles stay sentence-case; labels keep their original casing.
 */
export const formatFromTo = (labels) => {
  const { obstacles, qualities } = normalizeLabels(labels);
  const obsLine = obstacles.join(' & ');
  const quaLine = qualities.join(' & ');
  if (obsLine && quaLine) return `${obsLine}  →  ${quaLine}`;
  if (quaLine) return `A story of ${quaLine}`;
  if (obsLine) return `On ${obsLine}`;
  return '';
};

const fromToCaptionLine = (labels) => {
  const { obstacles, qualities } = normalizeLabels(labels);
  const obsLine = obstacles.map((o) => o.toLowerCase()).join(' and ');
  const quaLine = qualities.map((q) => q.toLowerCase()).join(' and ');
  if (obsLine && quaLine) return `From ${obsLine} to ${quaLine}`;
  if (obsLine) return `On ${obsLine}`;
  if (quaLine) return `A story of ${quaLine}`;
  return '';
};

/**
 * Platform-tuned caption.
 *
 * @param story  Story DTO (see lib/dto.js).
 * @param platform  'whatsapp' | 'linkedin' | 'instagram' | 'youtube' | 'twitter' | 'facebook' | 'generic'
 * @param url    Public story URL.
 * @param fromToLabels  { obstacle, quality } — pre-resolved display names.
 */
export const buildShareText = (story, platform, url, fromToLabels = null) => {
  if (!story) return '';
  const title = story.title || 'A story';
  const fromTo = fromToCaptionLine(fromToLabels);
  const excerpt = buildExcerpt(story);
  const author = authorSuffix(story);
  const headline = fromTo ? `${title}\n${fromTo}${author}` : `${title}${author}`;
  const tags = buildHashtags(story, fromToLabels);

  if (platform === 'linkedin') {
    return `${headline}\n\n"${excerpt}"\n\nRead the full story on Katha Mithra:\n${url}`;
  }
  if (platform === 'instagram') {
    return `${headline}\n\n"${excerpt}"\n\nRead on Katha Mithra:\n${url}\n\n${tags}`;
  }
  if (platform === 'youtube') {
    return `${headline}\n\n${excerpt}\n\nRead the full story: ${url}\n\n${tags}`;
  }
  if (platform === 'twitter') {
    // X has a 280-char limit; let intent strip whatever doesn't fit. Keep it
    // tight: title + short hook + URL is appended by the intent itself.
    const hook = fromTo ? ` — ${fromTo}` : '';
    return `${title}${hook}${author}`;
  }
  if (platform === 'facebook') {
    // sharer.php uses only the URL; this caption is for clipboard.
    return `${headline}\n\n"${excerpt}"\n\n${url}`;
  }
  // whatsapp / native / generic — uses WhatsApp's *bold* / _italic_ markdown.
  return `*${title}*${author}${fromTo ? `\n_${fromTo}_` : ''}\n\n"${excerpt}"\n\n${url}`;
};

/**
 * One short evocative line for a story card. Prefers the first sentence of
 * section_3 (the pivot) for sectioned stories, else the first sentence of
 * raw_text. Always trimmed at sentence/word boundary so it reads cleanly.
 */
export const getDefaultPullQuote = (story) => {
  if (!story) return '';
  // Author-curated highlight is already length-bounded at HIGHLIGHT_MAX
  // by the DB CHECK. Return it verbatim — clamping it again here was
  // chopping perfectly-crafted highlights mid-sentence on the share card.
  const highlight = story.highlight?.trim();
  if (highlight) return highlight;
  const sectioned = (story.sections?.length || 0) > 0;
  const source = sectioned
    ? story.story?.turning_point || story.sections[0]?.content
    : story.raw_text;
  const para = firstParagraph(source);
  if (!para) return '';
  const match = para.match(/^[^.!?]+[.!?]/);
  const sentence = match ? match[0].trim() : para;
  return truncateAtBoundary(sentence, PULL_QUOTE_MAX);
};

export const canUseNativeShare = () =>
  typeof navigator !== 'undefined' && typeof navigator.share === 'function';

export const canShareFiles = (files) => {
  if (typeof navigator === 'undefined' || typeof navigator.canShare !== 'function') {
    return false;
  }
  try {
    return navigator.canShare({ files });
  } catch {
    return false;
  }
};

// Only Facebook still uses a stable web-intent URL the dialog opens directly.
// WhatsApp / LinkedIn / X intents are built inline in StoryCardDialog so the
// caption can be edited by the user before the URL is constructed.
export const facebookShareUrl = (url) =>
  `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
